import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpFunctionResult } from '../../_interfaces/HttpFunctionResult';
import { HttpService } from '../http.service';
import dayjs from '../../_utils/dayjs.config';
import { StripeSubscriptionBase, StriptPaymentMethod, StripeStatusResponse, StripePrice, StripeInvoice, StripePortalType } from './payment.types';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  private _stripeSubscriptionBase: StripeSubscriptionBase

  constructor(
    private http: HttpService
  ) { }

  public get stripeSubscriptionBase(): StripeSubscriptionBase{
    return this._stripeSubscriptionBase
  }

  public set stripeSubscriptionBase(data: StripeSubscriptionBase){
    this._stripeSubscriptionBase = data
  }

  /**
   * Gibt die Informationen der bestehenden Subscription zurück
   * @returns 
   */
  public async getBasicSubscription(): Promise<HttpFunctionResult<StripeSubscriptionBase>>{

    const result: HttpFunctionResult<any> = await this.http.GET( {
      path: "payment/subscription",
      timeout: 20000,
      asDataProperty: false
    })
    if(!result.success) return {success: false}
    
    this._stripeSubscriptionBase = {
      priceId: result.data.items[0].plan.id,
      name: environment.stripeSubItems.find(s => s.id == result.data.items[0].plan.id)?.name,
      users: +result.data.items[0].quantity,
      withCustomerLogin: environment.stripeSubItems.find(ss => ss.id == result.data.items[0].plan.id)?.withCustomerLogin,
      singlePrice: +result.data.items[0].price.unitAmount,
      amount: (+result.data.items[0].price.unitAmount / 100) * +result.data.items[0].quantity,
      nextBill: dayjs(result.data.currentPeriodEnd).format("DD.MM.YYYY"),
      status: result.data.status,
      cancelAtPeriodEnd: result.data?.cancelAtPeriodEnd,
      canceledAt: result.data?.canceledAt ? dayjs(result.data.canceledAt) : null,
      cancelAt: result.data?.cancelAt ? dayjs(result.data.cancelAt) : null
    }
    return {success: true, data: this._stripeSubscriptionBase}
  }

  /**
   * Holt die default Zahlungsmethode des Kunden
   * @returns 
   */
  public async getPaymentMethod(): Promise<HttpFunctionResult<StriptPaymentMethod | null>>{
    const result: HttpFunctionResult<any> = await this.http.GET( {
      path: "payment/customer/payment-method",
      timeout: 20000,
      asDataProperty: false
    })
    if(!result.success) return {success: false}

    if(!result.data) return {success: true, data: null}

    let paymentMethod: StriptPaymentMethod
    if(result.data.type == "card") paymentMethod = {
      id: result.data.id,
      type: "card",
      brand: result.data.card.brand,
      last4: result.data.card.last4,
      expMonth: result.data.card.exp_month,
      expYear: result.data.card.exp_year
    }
    else if(result.data.type == "sepa_debit") paymentMethod = {
      id: result.data.id,
      type: "sepa_debit",
      country: result.data.sepaDebit.country,
      last4: result.data.sepaDebit.last4,
      bankCode: result.data.sepaDebit.bankCode
    }
    else if(result.data.type == "paypal") paymentMethod = {
      id: result.data.id,
      type: "paypal"
    }
    else return {success: false}

    return {success: true, data: paymentMethod}
    
  }

  
  public async getSubscriptionStatus(): Promise<HttpFunctionResult<StripeStatusResponse>>{
      const result: HttpFunctionResult<StripeSubscriptionBase> = await this.getBasicSubscription()
      if(!result.success) return {success: false}
  
      const endDate: dayjs.Dayjs = dayjs(result.data.nextBill, "DD.MM.YYYY")

      return {success: true, data: {
        daysToExpire: endDate.diff(dayjs().startOf('day'), 'day'),
        status: result.data.status
      }}
  }

  /**
   * Updatet die Anzahl der Benutzer bei Stripe
   * @param {number} amount Anzahl auf die es geändert werden soll
   * @returns 
   */
  public async updateUserAmount(data: {data: number, password: string}): Promise<HttpFunctionResult<any>>{
    return new Promise(async resolve=>{
      await this.http.PUT<{data: number, password: string}, any>({
        path: "payment/subscription/amount",
        body: data,
        statusResult: async (status: number, result: HttpFunctionResult<any>, err: HttpErrorResponse)=>{
          if(result.success) return resolve({success: true})
          else return resolve( {success: false, data: status})
        }
      })
    })
  }
  
  /**
   * Updatet das Modell eines Kunden
   * @param data 
   * @returns 
   */
  public async updateModell(data: {data: string, password: string}): Promise<HttpFunctionResult<any>>{
    return new Promise(async resolve=> {
      await this.http.PUT<{data: string, password: string}, any>({
        path: "payment/subscription/modell",
        body: data,
        statusResult: async (status: number, result: HttpFunctionResult<any>, err: HttpErrorResponse)=>{
          if(result.success) return resolve({success: true})
          else return resolve( {success: false, data: status})
        }
      })
    })
  }

  /**
   * Gibt die Preise der Produkte zurück
   * @returns 
   */
  public async getPrices(): Promise<HttpFunctionResult<StripePrice[]>>{
    const body: StripePrice[] = environment.stripeSubItems.map(s => ({priceId: s.id}))
    return await this.http.POST<StripePrice[], StripePrice[]>({
      path: "payment/prices",
      body: body
    })
  }

  /**
   * Gibt die bereits bezahlten Rechnungen zurück
   * @returns 
   */
  public async getInvoices(): Promise<HttpFunctionResult<StripeInvoice[]>>{
    return await this.http.GET<StripeInvoice[]>({
      path: "payment/invoices"
    })
  }

  /**
   * erstellt eine session für das Stripe Portal
   * @param flow 
   * @returns 
   */
  public async createPortalSession(flow: StripePortalType): Promise<HttpFunctionResult<{url: string}>>{
    return await this.http.GET({
      path: `payment/portalsession?sessionType=${flow}`,
    })
  }

  /**
   * erstellt eine stripe Portal session aber über benutzername und passwort
   * @param {StripePortalType} flow Welche Art von Portal Session erstellt werden soll
   * @param {Object} credentials Benutzername und Passwort
   */
  public async createPortalSessionWithCredetials(flow: StripePortalType, credentials: {username: string, password: string}): Promise<HttpFunctionResult<{url: string}>>{
    return await this.http.POST({
      path: `payment/portalsession/credentials?sessionType=${flow}`,
      body: credentials,
    })
  }


}
